import { Extension } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Plugin, PluginKey, Selection, TextSelection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

const getHintInsertPos = (doc: ProseMirrorNode): number | null => {
  let lastTextblockEnd: number | null = null;
  let lastNonEmptyTextblockEnd: number | null = null;

  doc.descendants((node, pos) => {
    if (!node.isTextblock) {
      return;
    }
    const endPos = pos + node.nodeSize - 1;
    lastTextblockEnd = endPos;
    if (node.textContent.trim().length > 0) {
      lastNonEmptyTextblockEnd = endPos;
    }
  });

  return lastNonEmptyTextblockEnd ?? lastTextblockEnd;
};

type HintPluginState = { active: boolean };
export const hintPluginKey = new PluginKey<HintPluginState>("textEditorHint");

/**
 * Renders a non-editable "hint" at the end of the editor content on focus.
 * - Shows only when the doc already has content
 * - Hides on any doc change or blur
 * - Moves caret to end on keyboard/tab focus (but not on mouse click focus)
 *
 * The hint is a decoration widget, so it never becomes part of the persisted doc/HTML/text.
 */
export const HintExtension = Extension.create<{ hint: string }>({
  name: "textEditorHint",
  addOptions() {
    return { hint: "" };
  },
  addProseMirrorPlugins() {
    const hint = this.options.hint?.trim();
    if (!hint) {
      return [];
    }

    return [
      new Plugin<HintPluginState>({
        key: hintPluginKey,
        state: {
          init: () => ({ active: false }),
          apply: (tr, value) => {
            const meta = tr.getMeta(hintPluginKey) as
              | { type: "focus" | "blur" }
              | undefined;
            if (meta?.type === "focus") {
              return { active: true };
            }
            if (meta?.type === "blur") {
              return { active: false };
            }
            if (tr.docChanged) {
              // Hide on change (don’t reinsert until next focus).
              return { active: false };
            }
            return value;
          },
        },
        props: {
          handleDOMEvents: {
            focus: (view) => {
              const tr = view.state.tr.setMeta(hintPluginKey, {
                type: "focus",
              });

              view.dispatch(tr);

              const moveCaretToEnd = () => {
                if (!view.hasFocus()) {
                  return;
                }
                const insertPos = getHintInsertPos(view.state.doc);
                const selection =
                  typeof insertPos === "number"
                    ? TextSelection.create(view.state.doc, insertPos)
                    : Selection.atEnd(view.state.doc);
                view.dispatch(
                  view.state.tr.setSelection(selection).scrollIntoView()
                );
              };

              // ProseMirror can still adjust the selection during its focus handling
              // (often placing the caret at the start). Schedule a couple of retries
              // after focus settles so we reliably land after the last character.
              requestAnimationFrame(() => {
                moveCaretToEnd();
              });
              return false;
            },
            blur: (view) => {
              view.dispatch(
                view.state.tr.setMeta(hintPluginKey, { type: "blur" })
              );
              return false;
            },
          },
          decorations: (state) => {
            const pluginState = hintPluginKey.getState(state);
            if (!pluginState?.active) {
              return null;
            }

            const insertPos = getHintInsertPos(state.doc);
            if (insertPos == null) {
              return null;
            }

            const dom = document.createElement("span");
            dom.className = "text-editor-hint";
            dom.textContent = hint;
            dom.setAttribute("contenteditable", "false");
            dom.setAttribute("data-text-editor-hint", "true");

            const deco = Decoration.widget(insertPos, dom, { side: 1 });
            return DecorationSet.create(state.doc, [deco]);
          },
        },
      }),
    ];
  },
});
